// ---------------------------------------------------------------------------
// Background AI tasks.
//
// Long generations (profile extraction, application intake, school fit,
// question research) run through here instead of being awaited inside a
// screen, so the user can keep navigating the app while the AI works. The
// task itself persists its results to the database and refreshes the store —
// it must never depend on the launching screen staying mounted.
//
// On completion the runner shows the global toast and (when the window is in
// the background) an OS notification. The global CodexActivityIndicator keeps
// showing live progress while the underlying Codex call runs.
//
// A `key` dedupes tasks: starting a task whose key is already running is
// refused, so double-clicks and navigate-away-and-back can't launch the same
// job twice. Screens subscribe via useBackgroundTask(key) to disable their
// buttons while "their" task runs — even after an unmount/remount.
// ---------------------------------------------------------------------------

import { useSyncExternalStore } from 'react';
import { useAppStore } from '../store/useAppStore';
import { notifyOs } from '../utils/notify';

type Listener = () => void;

const running = new Set<string>();
const listeners = new Set<Listener>();
// Monotonic version as the snapshot: useSyncExternalStore only needs a value
// that changes whenever the running set changes.
let version = 0;

function emit(): void {
  version++;
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function isTaskRunning(key: string): boolean {
  return running.has(key);
}

/**
 * Re-render whenever any background task starts or finishes. Use together
 * with isTaskRunning() when a screen checks many keys (e.g. one per list row).
 */
export function useBackgroundTasksVersion(): number {
  return useSyncExternalStore(
    subscribe,
    () => version,
    () => version,
  );
}

/** Reactive "is this task running?" — stays correct across unmount/remount. */
export function useBackgroundTask(key: string): boolean {
  useBackgroundTasksVersion();
  return running.has(key);
}

export interface BackgroundTaskOptions {
  /** Dedupe key, e.g. `research:prog_123`. One task per key at a time. */
  key: string;
  /** Human label used in failure messages, e.g. "Researching UF questions". */
  label: string;
  /**
   * The work. Persist all results to the DB and refresh the store inside —
   * the launching screen may be unmounted by the time this resolves. The
   * resolved string is the success message shown to the user.
   */
  run: () => Promise<string>;
}

/**
 * Fire-and-forget a background AI task. Returns false (and does nothing) if a
 * task with the same key is already running.
 */
export function runBackgroundTask(options: BackgroundTaskOptions): boolean {
  if (running.has(options.key)) return false;
  running.add(options.key);
  emit();
  void (async () => {
    const { showToast } = useAppStore.getState();
    try {
      const message = await options.run();
      showToast(message, 'success');
      void notifyOs('Dental Interview Coach', message);
    } catch (e) {
      const message = `${options.label} failed: ${(e as Error).message}`;
      showToast(message, 'error');
      void notifyOs('Dental Interview Coach', message);
    } finally {
      running.delete(options.key);
      emit();
    }
  })();
  return true;
}
